import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";

const LIMIT = 20;

/** Sets the sponsor of one member; their whole team moves with them. */
const AddSponsorModal = ({ row, onClose, onDone }) => {
  const [sponsor, setSponsor] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSponsor("");
  }, [row]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await apiCall("POST", `/change_sponsor?uid=${row.uid}`, { sponsor_username: sponsor.trim() });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not add the sponsor.");
    toast.success(data?.message || "Sponsor added.");
    onDone();
  };

  return (
    <Modal show={!!row} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Add sponsor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {row && (
            <>
              <div className="ar-member-card">
                <span className="ar-avatar">{String(row.name || row.username).slice(0, 2).toUpperCase()}</span>
                <div>
                  <div className="ps-strong">{row.name || row.username}</div>
                  <div className="ps-muted ps-small">{row.username}{row.email ? ` · ${row.email}` : ""}</div>
                </div>
                <div className="ar-member-amount">
                  <div className="ps-strong">{row.team_size}</div>
                  <div className="ps-muted ps-small">in their team</div>
                </div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>Sponsor username</Form.Label>
                <Form.Control value={sponsor} onChange={(e) => setSponsor(e.target.value.toUpperCase())} placeholder="e.g. AD1234567" required autoFocus />
                <Form.Text>
                  {row.sponsor_hint ? `The import list named ${row.sponsor_hint}, which was not found. ` : ""}
                  The member's team moves with them; team counts and ranks are updated.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" className="ar-primary-btn" disabled={saving || !sponsor.trim()}>
            {saving ? <Spinner size="sm" /> : "Add Sponsor"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/** Members that have no sponsor yet (e.g. imported without one). */
const WithoutSponsor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (applied) params.set("search", applied);
    const { data: res, error } = await apiCall("GET", `/members-without-sponsor?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load members.");
    setData(res?.data || null);
  }, [page, applied]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const rows = data?.rows || [];
  const total = data?.total || 0;

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>Members without Sponsor</h2>
          <p>These members have no sponsor yet. Add one here; members also see a popup after login asking for their sponsor.</p>
        </div>
        <ExportButtons
          title="Members without Sponsor"
          filters={applied ? `search: ${applied}` : ""}
          columns={[
            { header: "Name", value: (r) => r.name },
            { header: "Username", value: (r) => r.username },
            { header: "Email", value: (r) => r.email },
            { header: "Mobile", value: (r) => r.mobile },
            { header: "Joined", value: (r) => (r.joining_date ? moment(r.joining_date).format("DD MMM YYYY") : "") },
            { header: "Team", value: (r) => r.team_size },
            { header: "Sponsor in import list", value: (r) => r.sponsor_hint },
          ]}
          getRows={() =>
            collectPages(async (p, l) => {
              const params = new URLSearchParams({ page: p, limit: l });
              if (applied) params.set("search", applied);
              const { data: res } = await apiCall("GET", `/members-without-sponsor?${params.toString()}`);
              return { rows: res?.data?.rows || [], pages: res?.data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-stats ar-stats">
        <div className={`ps-stat ar-stat ${total ? "ar-stat--pending" : "ar-stat--approved"}`}>
          <span>Without sponsor</span>
          <strong>{data ? total : "—"}</strong>
          <small>{total ? "need a sponsor" : "every member has a sponsor"}</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Username, name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email / Mobile</th>
                <th>Joined</th>
                <th>Team</th>
                <th>Sponsor in import list</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="ps-empty">{applied ? "Nothing matches this search." : "Every member has a sponsor."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.uid}>
                    <td>
                      <div className="ps-strong">{r.name || "—"}</div>
                      <div className="ps-muted ps-small">{r.username}</div>
                    </td>
                    <td className="ps-small">
                      <div>{r.email || "—"}</div>
                      <div className="ps-muted">{r.mobile || ""}</div>
                    </td>
                    <td className="ps-small">{r.joining_date ? moment(r.joining_date).format("DD MMM YYYY") : "—"}</td>
                    <td>{r.team_size}</td>
                    <td className="ps-small">{r.sponsor_hint ? <span className="ps-muted">{r.sponsor_hint} (not found)</span> : "—"}</td>
                    <td className="text-end">
                      <Button size="sm" className="ar-primary-btn" onClick={() => setEditing(r)}>Add Sponsor</Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {data?.pages || 1}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= (data?.pages || 1)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <AddSponsorModal row={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
    </div>
  );
};

export default WithoutSponsor;
